import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useWindowDimensions } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { SpecialistFigure } from './SpecialistFigure';
import {
  AVATAR_VARIANTS,
  BADGE_BORDER_CLASS,
  BADGE_FILL_CLASS,
  BADGE_GLYPH_CLASS,
  RING_CLASS,
  SUBMITTER_VARIANT,
  specialistTone,
  specialistVariant,
} from './specialistAvatarTone';

import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';

/**
 * The avatar sizes, in px at the default text size. The answers draw 32 in a card header, beside a
 * follow-up reply. The smaller sizes are ring only, see `MIN_BADGE_SIZE`.
 */
export type SpecialistAvatarSize = 32 | 24 | 22 | 20;

const SIZE_CLASS: Record<SpecialistAvatarSize, string> = {
  32: 'h-8 w-8',
  24: 'h-6 w-6',
  22: 'h-[1.375rem] w-[1.375rem]',
  20: 'h-5 w-5',
};

/** The ring is 3px around a header avatar and 2px around the small ones, so it reads at 20px. */
const RING_WIDTH_CLASS: Record<SpecialistAvatarSize, string> = {
  32: 'border-[3px]',
  24: 'border-2',
  22: 'border-2',
  20: 'border-2',
};

/**
 * The smallest avatar that carries the number badge. At 20px and 22px the badge would be about 10px with a
 * 7px numeral, which does not read, so the small avatars are ring only and the number is in the
 * name beside them, "Primum Specialist 2", which is always there.
 */
export const MIN_BADGE_SIZE: SpecialistAvatarSize = 24;

/** The badge's box, in rem on web and in points on a phone. Its numeral is `text-2xs`, 10px. */
const BADGE: Record<SpecialistAvatarSize, { box: string; native: number }> = {
  32: { box: 'h-4 w-4', native: 16 },
  24: { box: 'h-3.5 w-3.5', native: 14 },
  22: { box: 'h-3 w-3', native: 12 },
  20: { box: 'h-3 w-3', native: 12 },
};

/** The most the avatar grows with the reader's text size on a phone, as a multiple. */
const MAX_TEXT_SCALE = 2;

type Props = { size?: SpecialistAvatarSize } & (
  | {
      role: 'specialist';
      /** The number in the answer's name, `Primum Specialist 2`. It picks the color and is drawn. */
      position: number;
      /**
       * False leaves the number badge off at any size, for a reply, where the name beside the
       * avatar already carries the number.
       */
      badge?: boolean;
    }
  | { role: 'submitter' }
);

/**
 * The avatar for a Primum Specialist or the submitter: the app's doctor placeholder redrawn in one
 * of the brand variants of `specialistAvatarTone.ts`, see `SpecialistFigure`, inside a ring of the
 * variant's ring color with a 1px gap of the surface, so the ring reads apart from the circle.
 *
 * A Primum Specialist is the base variant for its number, blue, green or orange, and from 24px up
 * also carries its number in a small badge at the bottom right, filled in its circle color, with a
 * thin `background-0` border that parts it from the figure. The same specialist looks the same in
 * its card header and every reply. The submitter is the pale green inverse with no
 * ring and no badge, so a ring always means a specialist.
 *
 * It is decorative and hidden from screen readers, because the name beside it says who is
 * speaking, and the name is always there, so identity never rests on color or the badge. It
 * grows with the reader's text size: in rem on web, so it follows the browser's text size, and by
 * the system font scale on a phone, to at most twice its size, with the numeral capped the same.
 */
export function SpecialistAvatar(props: Props) {
  const { size = 32 } = props;

  const { fontScale } = useWindowDimensions();

  // On web the tailwind size is in rem and follows the browser. On a phone it is set in points.
  const scale = Math.min(Math.max(fontScale, 1), MAX_TEXT_SCALE);

  const nativeSize = size * scale;

  const variantName =
    props.role === 'specialist' ? specialistVariant(props.position) : SUBMITTER_VARIANT;

  const variant = AVATAR_VARIANTS[variantName];

  /** Only a specialist carries a badge, filled in its circle color. */
  const tone =
    props.role === 'specialist' && props.badge !== false ? specialistTone(props.position) : null;

  const label = props.role === 'specialist' ? String(props.position) : '';

  const badge = BADGE[size];

  // Native only: the web Text is a DOM element, which has no such attribute and warns on it.
  const glyphScale = isWeb ? {} : { maxFontSizeMultiplier: MAX_TEXT_SCALE };

  return (
    <View
      aria-hidden
      accessible={false}
      importantForAccessibility="no-hide-descendants"
      accessibilityElementsHidden
      className={twJoin('relative shrink-0', SIZE_CLASS[size])}
      style={isWeb ? undefined : { width: nativeSize, height: nativeSize }}>
      <View
        className={
          props.role === 'specialist'
            ? twJoin(
                'h-full w-full rounded-full p-px',
                RING_WIDTH_CLASS[size],
                RING_CLASS[variantName]
              )
            : 'h-full w-full rounded-full'
        }>
        <SpecialistFigure circle={variant.circle} figure={variant.figure} />
      </View>

      {tone && size >= MIN_BADGE_SIZE ? (
        <View
          className={twJoin(
            'absolute -bottom-0.5 -right-0.5 items-center justify-center rounded-full border',
            badge.box,
            BADGE_FILL_CLASS[tone],
            BADGE_BORDER_CLASS
          )}
          style={isWeb ? undefined : { width: badge.native * scale, height: badge.native * scale }}>
          <Text
            size="2xs"
            {...glyphScale}
            className={twJoin(
              'text-center font-body font-bold web:leading-none',
              BADGE_GLYPH_CLASS[tone]
            )}>
            {label}
          </Text>
        </View>
      ) : null}
    </View>
  );
}
